import type { CSSProperties, ReactNode } from "react";
import React from "react";

import "./kit.css";

/** CSSProperties plus the kit's custom-property slots (lint-safe, no casts). */
export interface T_KitCssVars extends CSSProperties {
  "--lk-bg"?: string;
  "--lk-bg-hover"?: string;
  "--lk-bg-pressed"?: string;
  "--lk-fg"?: string;
}

type T_InteractiveSurfaceProps = {
  ariaBusy?: boolean;
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaLabel?: string;
  ariaPressed?: boolean;
  backgroundColor: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick: () => void;
  pressedBackgroundColor: string;
  style?: CSSProperties;
  type?: "button" | "submit";
};

/**
 * @storybook-internal
 *
 * Internal interaction chassis for shared controls — web mirror of the mobile
 * InteractiveSurface. It owns the app-wide press fill/scale transition so
 * buttons cannot drift into one-off pressed-state behavior. Divergences from
 * mobile (recorded in the ui-catalog manifest): no haptics; hover and
 * focus-visible states exist only here.
 */
export function InteractiveSurface({
  ariaBusy,
  ariaControls,
  ariaExpanded,
  ariaLabel,
  ariaPressed,
  backgroundColor,
  children,
  className,
  disabled = false,
  onClick,
  pressedBackgroundColor,
  style,
  type = "button",
}: T_InteractiveSurfaceProps) {
  // Hover intentionally reuses the pressed fill (see kit.css fallback);
  // a distinct hover fill has no mobile analogue and stayed dead code.
  const vars: T_KitCssVars = {
    "--lk-bg": backgroundColor,
    "--lk-bg-pressed": pressedBackgroundColor,
  };

  return (
    <button
      aria-busy={ariaBusy}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      className={["lk-interactive", className].filter(Boolean).join(" ")}
      disabled={disabled}
      onClick={onClick}
      style={{ ...vars, ...style }}
      type={type}
    >
      {children}
    </button>
  );
}
